<template>
	<div>
		<div
			class="modal-container modal-info modal-large modal-help"
			:style="{ zIndex: '9999999999' }"
		>
			<div class="modal-header">
				Support Ticket History
				<div style="display: flex; align-items: center; justify-content: space-between">
					<div style="margin-right: 10px">Create New Ticket</div>
					<button
						class="modal-close"
						@click="openHelpModal"
						style="width: 60px; height: 35px; display: flex; align-items: center; justify-content: center"
					>
						<i class="fas fa-plus"></i>
					</button>
				</div>
			</div>
			<div class="modal-body">
				<IsBusySectionComponent v-if="isBusy" />
				<RichTable
					v-else-if="currentIssues.length > 0"
					:tableData="currentIssues"
					:headers="headers"
				>
					<template #notification="row">
						<div style="position: relative; display: inline-block">
							<button
								class="button-search"
								@click="openHelpInfoModal(row)"
							>
								<i class="fas fa-pen"></i>
							</button>
							<span
								v-if="row.showNotification"
								class="notification-bubble"
							></span>
						</div>
					</template>
				</RichTable>
				<div v-else>
					<div
						class="no-data-message"
						style="text-align: center"
					>
						No support tickets found. Click the '+' button above to create your first ticket.
					</div>
				</div>
			</div>
			<div class="modal-footer">
				<button
					class="button-save"
					:disabled="isBusy"
					style="background-color: var(--error-color)"
					@click="close()"
				>
					Close
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted, Ref, inject } from 'vue';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import { EventBusCore } from '@core/services/util';
	import IsBusySectionComponent from '@core/components/IsBusySectionComponent.vue';
	import modalHelp from '@core/modals/modalHelp.vue';
	import modalHelpInfo from '@/modals/modalHelpInfo.vue';
	import RichTable from '@core/components/RichTable.vue';

	interface Props {
		modal: {
			passedData: {
				route?: string;
				login: boolean;
				username?: string;
			};
		};
	}

	const hasHelpNotification = inject<Ref<boolean>>('hasHelpNotification', null);

	const route = ref<string>(null);
	const login = ref(false);
	const username = ref<string>(null);
	const currentIssues = ref<any[]>([]);
	const isBusy = ref(false);

	const props = defineProps<Props>();

	const user = computed(() => {
		if (login.value) {
			return auth?.getTokenPayload();
		}
		return null;
	});

	const headers = ref([
		{
			name: 'created',
			display: 'Date',
			sortable: true,
			value: (row: any) => (row.fields?.created ? new Date(row.fields?.created).toLocaleDateString() : ''),
			sortValue: (row: any) => (row.fields?.created ? new Date(row.fields?.created).getTime() : 0),
		},
		{
			name: 'summary',
			display: 'Summary',
			sortable: true,
			value: (row: any) => row.fields?.summary,
			sortValue: (row: any) => row.fields?.summary?.toLowerCase() || '',
		},
		{
			name: 'type',
			display: 'Type',
			sortable: true,
			value: (row: any) => row.fields?.issueType?.name,
			sortValue: (row: any) => row.fields?.issueType?.name?.toLowerCase() || '',
		},
		{
			name: 'status',
			display: 'Status',
			sortable: true,
			value: (row: any) => getStatus(row),
			sortValue: (row: any) => getStatus(row)?.toLowerCase() || '',
		},
		{
			name: 'action',
			display: 'Edit',
			sortable: false,
			slot: 'notification',
		},
	]);

	const getHelpHistory = async () => {
		isBusy.value = true;
		const request = {
			userEmail: user.value?.EmployeeEmail,
		};
		const response = await api.utilities.getHelpHistory(request);
		currentIssues.value = response.data.issues;
		getHelpNotification();
		isBusy.value = false;
		return response.data;
	};

	const getHelpNotification = () => {
		currentIssues.value = currentIssues.value.map(issue => {
			const storageKey = `helpNotification-${issue.key}`;
			const hasNotification = localStorage.getItem(storageKey);
			return {
				...issue,
				showNotification: hasNotification,
			};
		});
	};

	onMounted(() => {
		route.value = props.modal.passedData.route ?? window.location.href;
		login.value = props.modal.passedData.login;
		getHelpHistory();
	});

	//open helpmodal
	const openHelpInfoModal = (row: any) => {
		localStorage.removeItem(`helpNotification-${row.key}`);
		row._ref.showNotification = false;
		hasHelpNotification.value = Object.keys(localStorage).some(key => key.startsWith('helpNotification-'));
		$modal.open(modalHelpInfo, {
			name: 'modalHelpInfo',
			passedData: {
				row: row,
			},
			postFunction: (reloadTable: boolean) => {
				if (reloadTable) {
					getHelpHistory();
				}
			},
		});
	};

	const getStatus = (row: any) => {
		if (row.fields?.status.statusCategory.name === 'To Do' || row.fields?.status.statusCategory.name === 'In Progress') {
			return 'Open';
		} else if (row.fields?.status.statusCategory.name === 'Done' || row.fields?.status.statusCategory.name === 'Closed') {
			return 'Done';
		} else {
			return 'Open';
		}
	};

	const openHelpModal = () => {
		$modal.open(modalHelp, {
			name: 'modalHelp',
			passedData: {
				route: route.value,
				login: login.value,
				username: username.value,
			},
			postFunction: async (reloadTable: boolean) => {
				if (reloadTable) {
					// prevents the table from being empty when the modal is opened
					isBusy.value = true;
					// fix silly jira racecondition. dont think too much
					setTimeout(async () => {
						await getHelpHistory();
						isBusy.value = false;
					}, 1500);
				}
			},
			backdrop: false,
		});
	};

	const close = () => {
		EventBusCore.emit('helpOpen');
		$modal.close('modalHelpHistory');
	};
</script>
<style scoped></style>
